import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from './user.service';
import { ProfileCompletionModalComponent } from '../components/profile-completion-modal/profile-completion-modal.component';

const SKIP_COUNT_KEY = 'profileSkipCount';
const LAST_SKIPPED_KEY = 'profileLastSkipped';

@Injectable({
    providedIn: 'root'
})
export class ProfileCompletionService {

    // Delays in minutes
    private readonly skipDelays = [60, 30, 10, 5];

    constructor(
        private dialog: MatDialog,
        private userService: UserService
    ) { }

    public checkProfileCompletion(): void {
        const user = this.userService.getCurrentUserValue();

        if (!user) {
            // This can happen on initial load before the user is set.
            // The check will be re-triggered by AppComponent once the user is fetched.
            return;
        }
        this.evaluateProfile(user);
    }

    private evaluateProfile(user: any): void {
        // Exit if the user is a manager or lead, as they don't need to complete a company profile.
        if (user.role?.includes('manager') || user.role?.includes('lead')) {
            return;
        }

        // Only proceed to check for profile completion if the user has the 'company' role.
        if (user.role?.includes('company')) {
            const isProfileComplete = user.meta?.company_name?.trim() &&
                user.meta?.phone_number?.trim() &&
                user.meta?.teamSize?.trim();

            if (isProfileComplete) {
                this.clearSkipData();
                return;
            }

            const lastSkipped = localStorage.getItem(LAST_SKIPPED_KEY);
            if (lastSkipped) {
                const skipCount = parseInt(localStorage.getItem(SKIP_COUNT_KEY) || '0', 10);
                const delayIndex = Math.min(skipCount, this.skipDelays.length - 1);
                const delay = this.skipDelays[delayIndex] * 60 * 1000;
                const timeSinceSkipped = Date.now() - new Date(lastSkipped).getTime();

                if (timeSinceSkipped < delay) {
                    return; // Not time to show the modal yet.
                }
            }

            this.openProfileCompletionModal();
        }
    }

    public openProfileCompletionModal(): void {
        // Check if a dialog is already open.
        if (this.dialog.openDialogs.length > 0) {
            return;
        }
        this.dialog.open(ProfileCompletionModalComponent, {
            width: '500px',
            disableClose: true,
            autoFocus: false
        });
    }

    public recordSkip(): void {
        const skipCount = parseInt(localStorage.getItem(SKIP_COUNT_KEY) || '0', 10);
        localStorage.setItem(SKIP_COUNT_KEY, (skipCount + 1).toString());
        localStorage.setItem(LAST_SKIPPED_KEY, new Date().toISOString());
    }

    public clearSkipData(): void {
        localStorage.removeItem(SKIP_COUNT_KEY);
        localStorage.removeItem(LAST_SKIPPED_KEY);
    }
}
